import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, X, RefreshCw } from "lucide-react";
import axios from "axios";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("aa_ops_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const RESET_PHRASE = "RESET DEMO DATA";

const CountsGrid = ({ counts }) => {
  if (!counts) return null;
  const entries = Object.entries(counts).filter(([, v]) => typeof v === "number");
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
      {entries.map(([k, v]) => (
        <div key={k} className="rounded-sm border border-coach-silver p-2.5 bg-white" data-testid={`demo-count-${k}`}>
          <div className="text-[10.5px] uppercase tracking-widest text-steel">{k.replaceAll("_", " ")}</div>
          <div className="text-[16px] font-semibold text-carbon tabular">{v.toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
};

const ConfirmModal = ({ open, loading, total, onCancel, onConfirm }) => {
  const [phrase, setPhrase] = useState("");
  useEffect(() => { if (!open) setPhrase(""); }, [open]);
  if (!open) return null;
  const armed = phrase === RESET_PHRASE;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="demo-reset-modal">
      <button aria-label="Close" className="absolute inset-0" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-md overflow-hidden bg-white border border-coach-silver">
        <div className="px-5 py-4 border-b border-coach-silver flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-critical-red" />
            <div className="text-[15px] font-semibold text-carbon">Reset Demo Data?</div>
          </div>
          <button onClick={onCancel} className="w-8 h-8 grid place-items-center text-steel">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[13.5px] text-carbon">
            This will permanently remove the seeded/demo business records from Preview. Your users, roles,
            branding, templates and system configuration will remain.
          </p>
          <p className="text-[12.5px] text-steel">
            Approximately <strong className="text-carbon">{(total || 0).toLocaleString()}</strong> demo records will be deleted.
          </p>
          <label className="block">
            <div className="text-[11.5px] uppercase tracking-wide text-steel mb-1">
              Type <code className="text-carbon">{RESET_PHRASE}</code> to confirm
            </div>
            <input
              data-testid="demo-reset-phrase"
              autoFocus
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 font-mono text-[13px]"
            />
          </label>
        </div>
        <div className="px-5 py-4 border-t border-coach-silver flex items-center justify-end gap-2">
          <button onClick={onCancel} className="h-10 px-4 rounded-sm text-[13px] text-steel">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={!armed || loading}
            data-testid="demo-reset-confirm"
            className="h-10 px-5 rounded-sm text-[13px] font-semibold text-white disabled:opacity-40"
            style={{ background: "#B83C3C" }}
          >
            {loading ? "Resetting…" : "Reset Demo Data"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function SystemSettingsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const isAdminTier = isSuperAdmin || user?.role === "admin";
  const [counts, setCounts] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [busy, setBusy] = useState(false);
  // Production hides the Demo Reset surface entirely. Preview / dev
  // keeps it. Environment is served by the API root; treat any
  // "production" or "prod" value as production and default-hide on
  // network error so we never accidentally show the tool.
  const [envAllowsDemoReset, setEnvAllowsDemoReset] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/");
        const env = String(r.data?.environment || "").toLowerCase();
        if (!cancelled) setEnvAllowsDemoReset(!(env === "production" || env === "prod"));
      } catch {
        if (!cancelled) setEnvAllowsDemoReset(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const showDemoReset = isSuperAdmin && envAllowsDemoReset;

  const load = async () => {
    if (!showDemoReset) return;
    setLoading(true);
    try {
      const r = await api.get("/admin/demo-data-counts");
      setCounts(r.data.counts);
      setTotal(r.data.total);
    } catch (e) {
      toast.error("Failed to load demo counts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showDemoReset]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/demo-data/reset", { confirmation: RESET_PHRASE });
      const deleted = Object.values(r.data.deleted || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Demo data reset — ${deleted.toLocaleString()} records removed`);
      setModal(false);
      await load();
    } catch (e) {
      toast.error("Reset failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">System Settings</SectionHeader>
      <PageHeader
        testid="page-system-settings"
        backTo={{ label: "Back to Administration", to: "/administration" }}
        title="System Settings"
        subtitle="Organization-level configuration"
      />

      {/* Quote Footer Templates — Admin (admin.system_config) */}
      {isAdminTier ? (
        <QuoteFooterTemplatesCard />
      ) : (
        <div className="rounded-md p-6 bg-white border border-coach-silver text-[13.5px] text-steel">
          System Settings are available to Admin users only.
        </div>
      )}

      {/* Demo Data Reset — Super Admin only, Preview/dev only */}
      {showDemoReset && (
        <div className="rounded-md p-5 bg-white border border-coach-silver mt-6" data-testid="demo-reset-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[14.5px] font-semibold text-carbon">Demo Data Reset</div>
              <div className="text-[12.5px] text-steel mt-1">
                Removes seeded/demo business records from Preview. Users, roles, contract templates,
                checklist templates, integration configuration and branding are preserved.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={load}
                disabled={loading}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm text-[12.5px] font-medium text-steel border border-coach-silver hover:bg-chrome-mist disabled:opacity-60"
                data-testid="demo-reset-refresh"
              >
                <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
              <button
                onClick={() => setModal(true)}
                data-testid="demo-reset-open"
                className="inline-flex items-center h-9 px-4 rounded-sm text-[12.5px] font-semibold text-critical-red border border-critical-red hover:bg-critical-red/5"
              >
                Reset Demo Data
              </button>
            </div>
          </div>
          <div className="mt-4 text-[11.5px] uppercase tracking-widest text-steel">
            Approximate counts (documents with <code>is_demo=true</code>) — total <strong className="text-carbon">{total.toLocaleString()}</strong>
          </div>
          <CountsGrid counts={counts} />
        </div>
      )}
      <ConfirmModal
        open={modal}
        loading={busy}
        total={total}
        onCancel={() => setModal(false)}
        onConfirm={confirm}
      />
    </PageShell>
  );
}

const QuoteFooterTemplatesCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [standard, setStandard] = useState("");
  const [nonAi, setNonAi] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/administration/organization-settings/quote-footer-templates");
      setStandard(r.data.standard_quote_footer_template || "");
      setNonAi(r.data.non_all_inclusive_quote_footer_template || "");
      setUpdatedAt(r.data.updated_at || null);
    } catch (e) {
      toast.error("Failed to load Quote Footer Templates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(
        "/administration/organization-settings/quote-footer-templates",
        {
          standard_quote_footer_template: standard,
          non_all_inclusive_quote_footer_template: nonAi,
        },
      );
      setUpdatedAt(r.data.updated_at || null);
      toast.success("Quote Footer Templates saved. Existing sent quotes are unchanged.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        toast.error("You don't have permission to edit these templates.");
      } else {
        toast.error("Save failed: " + (detail || e.message));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md p-5 bg-white border border-coach-silver" data-testid="quote-footer-templates-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[14.5px] font-semibold text-carbon">Quote Footer Templates</div>
          <div className="text-[12.5px] text-steel mt-1">
            Default footers applied to quotes at generation time. A per-tour Quote Footer on the Tour
            record overrides the org template. Editing here NEVER changes previously sent quotes.
          </div>
        </div>
        {updatedAt && (
          <div className="text-[10.5px] uppercase tracking-widest text-steel">
            Last saved {new Date(updatedAt).toLocaleString()}
          </div>
        )}
      </div>
      {loading ? (
        <div className="text-[12.5px] text-steel mt-4">Loading…</div>
      ) : (
        <div className="mt-4 space-y-3">
          <label className="block">
            <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">
              Standard (default) footer
            </div>
            <textarea
              data-testid="qft-standard"
              value={standard}
              onChange={(e) => setStandard(e.target.value)}
              rows={3}
              className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 text-[13px]"
              placeholder="e.g. Deposit due within 5 business days of acceptance."
            />
            <div className="text-[10.5px] text-steel mt-1">Applied when the tour's Contract Type is not "non_all_inclusive".</div>
          </label>
          <label className="block">
            <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">
              Non-All-Inclusive footer
            </div>
            <textarea
              data-testid="qft-non-all-inclusive"
              value={nonAi}
              onChange={(e) => setNonAi(e.target.value)}
              rows={3}
              className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 text-[13px]"
              placeholder="e.g. Fuel, tolls and driver per diem billed separately."
            />
            <div className="text-[10.5px] text-steel mt-1">Applied when the tour's Contract Type is "non_all_inclusive".</div>
          </label>
          <div className="flex justify-end">
            <button
              data-testid="qft-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center h-9 px-4 rounded-sm text-[12.5px] font-semibold text-white bg-coach-blue hover:bg-deep-road-navy disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save Templates"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
